
import {HttpClient,  HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HubConnectionState } from '@microsoft/signalr';
import { Store } from '@ngrx/store';
import { BehaviorSubject, debounceTime, Observable } from 'rxjs';
import { AuthService } from 'src/app/core/auth/auth.service';
import { HubActions } from 'src/app/state/hub.actions';
import { environment } from 'src/environments/environment';
import { FileRef } from '../model/fileDto';
import { MessageRecipientDto } from '../model/message-recipientDto';
import { MessageDto } from '../model/messages/message';
import { MessageReply } from '../model/messages/message-reply';
import { MessageRoomDto } from '../model/messages/message-room.dto';
import { SetMsgToSeenDto } from '../model/messages/set-msg-to-seen-dto';
import { User } from '../model/user';
import { ApiService } from './http.service';
import { HubServiceBase } from './hub-service-base';
import { RoomService } from './room.service';
export interface PaginatedMessageResponse {
  messages: MessageDto[];
  hasMore: boolean;
}
@Injectable({
  providedIn: 'root',
})
export class MessageService extends HubServiceBase {
  private messageRoutes = environment.api.messages;
  private unseenMessagesSource = new BehaviorSubject<number>(0);
  public unseenMessages$ = this.unseenMessagesSource.asObservable();
  private roomMessages = new Map<number, BehaviorSubject<MessageDto[]>>();
  private roomsSubject = new BehaviorSubject<MessageRoomDto[]>([]);
  public rooms$ = this.roomsSubject.asObservable();

  constructor(
    private api: ApiService,
    private http: HttpClient,
    public router: Router,
    private auth: AuthService,
    private roomService: RoomService,
    private store: Store<{ hub: { connected: boolean }; currentUser: User }>,
  ) {
    super();
    this.createConnection(environment.hub.message, () => this.auth.accessToken);
    this.getConnectionState$().subscribe((state) => {
      this.store.dispatch(
        HubActions.updateStatus({
          isConnected: state === HubConnectionState.Connected,
        }),
      );
    });
  }

  public async ensureConnection(): Promise<void> {
    if (!this.hubConnection) {
      this.createConnection(environment.hub.message, () => this.auth.accessToken);
    }
    if (this.hubConnection!.state !== HubConnectionState.Connected) {
      try {
        await this.start();
      } catch (err) {
        console.error('Failed to establish SignalR connection:', err);
        throw err;
      }
    }
  }

  public countUnseenMessages(userId: number): void {
    if (this.getState() === HubConnectionState.Connected) {
      this.hubConnection!.invoke('CountUnseen', userId).catch(console.error);
    }
  }

    public sendMessage(text: string, roomId: number, files: FileRef[] = []): Promise<boolean> {
      if ((!text || text.trim() === '') && files.length < 1) {return Promise.resolve(false);}
      if (this.getState() !== HubConnectionState.Connected) {return Promise.resolve(false);}
  
      return this.hubConnection!.invoke('SendMessage', text, roomId, files)
      .then(()=>true)
      .catch((err) =>{
        console.error('SendMessage failed:', err);
        return false;
      });
    }

  async markMessagesAsSeen(
    roomId: number,
    otherUserId: number,
    messageIds: number[] = [],
  ): Promise<void> {
    try {
      await this.ensureConnection();
      if (messageIds.length > 0 && messageIds.length < 20) {
        await this.hubConnection!.invoke('SetMessagesToSeen', messageIds);
      } else {
        await this.hubConnection!.invoke('SetAllMessagesInRoomToSeen', roomId, otherUserId);
      }
    } catch (err) {
      console.error('Error marking messages as seen:', err);
    }
  }

  override start(): Promise<void> {
    this.hubConnection!.on('MessagesCounted', (count: number) => {
      this.unseenMessagesSource.next(count);
    });

    this.hubConnection!.on('SendMessageResponse', (message: MessageDto) => {
      this.addMessageToRoom(message);
    });

    this.hubConnection!.on('MsgWithMetaResponse', (message: MessageDto ) => {
      if(message.metadataDTO){
        this.updateMsgWithMeta(message);
      }   
    });

    this.hubConnection!.on('MsgSentConfirmation', (message: MessageDto) => {
      this.addMessageToRoom(message);
    });

    this.hubConnection!.on(
      'RecipientOffline',
      (messageId: number, authorId: number, text: string, files: FileRef[]) => {
        const message: MessageDto = {
          id: messageId.toString(),
          userId: authorId,
          text,
          files,
        } as MessageDto;
        this.addMessageToRoom(message);
      },
    );

    this.hubConnection!.on('MessageSeen', (seenConfirmation: SetMsgToSeenDto) => {
      this.handleSeenConfirmation(seenConfirmation);
    });
    return super.start();
  }

  private getOrCreateRoomSubject(roomId: number): BehaviorSubject<MessageDto[]> {
    let subj = this.roomMessages.get(roomId);
    if (!subj) {
      subj = new BehaviorSubject<MessageDto[]>([]);
      this.roomMessages.set(roomId, subj);
    }
    return subj;
  }

  public getMessagesForRoom(roomId: number): Observable<MessageDto[]> {
    return this.getOrCreateRoomSubject(roomId).asObservable().pipe(debounceTime(100));
  }

  public getMessagesForRoomSync(roomId: number): MessageDto[] {
    return this.roomMessages.get(roomId)?.getValue() ?? [];
  }

  public seedInitialMessages(roomId: number, msgs: MessageDto[]) {
    this.getOrCreateRoomSubject(roomId).next([...msgs]);
  }

  addMessageToRoom(msg: MessageDto) {
    const subj = this.getOrCreateRoomSubject(msg.messageRoomId);
    const current = subj.value;
    if (current.some((m) => m.id === msg.id)) return;
    subj.next([...current, msg]);

    this.roomService.updateRoomPreview(msg.messageRoomId, msg);
  }

  private handleSeenConfirmation(seenConfirmation: SetMsgToSeenDto) {
    const roomId = seenConfirmation.roomId;
    const subject = this.roomMessages.get(roomId);
    if (!subject) return;

    const currentMessages = subject.value;
    let hasChanges = false;
    const updated = currentMessages.map((m) => {
      if (
        seenConfirmation.seenAllInRoom ||
        (m.id == seenConfirmation.msgId.toString() && !m.seen)
      ) {
        hasChanges = true;
        return { ...m, seen: true };
      }
      return m;
    });
    if (!hasChanges) {
      return;
    }
    subject.next(updated);
  }

  getActiveUsers() {
    return this.api.Get<MessageRecipientDto[]>(this.messageRoutes.getConnectedUsers);
  }

  getLimitedMessagesInRoom(
    id: number,
    limit: number,
    offset: number,
  ): Observable<PaginatedMessageResponse> {
    const params = new HttpParams().set('id', id).set('limit', limit).set('offset', offset);

    return this.api.Get<PaginatedMessageResponse>(this.messageRoutes.room.getMessages, { params });
    //return this.api.Get<MessageDto[]>(this.messageRoutes.room.getMessages, {params: {id, limit, offset}});
  }

  replyToMessage(messageReply: MessageReply): Observable<MessageReply> {
    return this.api.Post(this.messageRoutes.replyToMessage, messageReply);
  }

  downloadMessageImage(
    roomId: number,
    messageId: number,
    fileId: string,
  ): Observable<Blob> {
    return this.http.get(
      `${environment.api.basePath}/Room/${roomId}/messages/${messageId}/files/${fileId}/download`,
      {
        responseType: "blob",
      },
    );
  }

  updateMsgWithMeta(msg: MessageDto){
     const subj = this.getOrCreateRoomSubject(msg.messageRoomId);
     const current = subj.value;
     const updated = current.map(m => m.id === msg.id ? {...m, metadataDTO: msg.metadataDTO}:m);
     subj.next(updated);
     this.roomMessages.set(msg.messageRoomId,subj);
  }

}
